/** SUNTRONIX ONE — Category & Brand Masters (Configuration Studio).
 *  One source of truth (db.categories / db.brands). Full lifecycle:
 *  create · edit · activate/deactivate · safe-delete · reassign · history.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Power, Trash2, History, ArrowRightLeft, Layers, Tag, Loader2 } from "lucide-react";

function MasterTable({ kind, icon: Icon, singular }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);     // {id?, name, display_name, description}
  const [reassign, setReassign] = useState(null);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/catalogue-masters/${kind}`).then(({ data }) => setRows(data.items || []))
      .catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      if (edit.id) {
        await api.patch(`/catalogue-masters/${kind}/${edit.id}`,
          { name: edit.name, display_name: edit.display_name, description: edit.description });
        toast.success(`${singular} updated`);
      } else {
        await api.post(`/catalogue-masters/${kind}`,
          { name: edit.name, display_name: edit.display_name, description: edit.description });
        toast.success(`${singular} created`);
      }
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (r) => {
    try {
      await api.post(`/catalogue-masters/${kind}/${r.id}/status`, { active: !r.active });
      toast.success(r.active ? `${singular} deactivated` : `${singular} activated`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (r) => {
    try {
      await api.delete(`/catalogue-masters/${kind}/${r.id}`);
      toast.success(`${singular} deleted`); load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Delete failed";
      toast.error(msg);
      if (e.response?.status === 409) setReassign({ src: r, target_id: "", then: "deactivate" });
    }
  };

  const doReassign = async () => {
    if (!reassign.target_id) { toast.error("Pick a target"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/catalogue-masters/${kind}/${reassign.src.id}/reassign`,
        { target_id: reassign.target_id, then: reassign.then });
      toast.success(`Moved ${data.moved} product(s) → ${data.target}, source ${data.action}`);
      setReassign(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reassign failed"); }
    finally { setBusy(false); }
  };

  const openHistory = async (r) => {
    try {
      const { data } = await api.get(`/catalogue-masters/${kind}/${r.id}/history`);
      setHistory({ name: data.name, items: data.history || [] });
    } catch { toast.error("Failed to load history"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-slate-500">{rows.length} {kind}</div>
        <Button variant="create" size="sm" onClick={() => setEdit({ name: "", display_name: "", description: "" })}
          data-testid={`cm-add-${kind}`} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-1" />Add {singular}
        </Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid={`cm-table-${kind}`}>
          <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            {["Name", "Code", "Products", "Status", "Actions"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">None yet.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`cm-row-${kind}-${r.id}`}>
                <td className="px-3 py-2 text-slate-800 flex items-center gap-2">
                  <Icon className="h-3.5 w-3.5 text-slate-400" />{r.name}
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{r.code}</td>
                <td className="px-3 py-2 tabular-nums text-slate-600">{r.usage_count}</td>
                <td className="px-3 py-2">
                  <span className={"text-[10px] font-semibold px-1.5 py-0.5 rounded border " +
                    (r.active ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-100 border-slate-300 text-slate-500")}>
                    {r.active ? "ACTIVE" : "INACTIVE"}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <button title="Edit" onClick={() => setEdit({ id: r.id, name: r.name, display_name: r.display_name || "", description: r.description || "" })}
                      data-testid={`cm-edit-${r.id}`} className="p-1 text-slate-500 hover:text-slate-900"><Pencil className="h-3.5 w-3.5" /></button>
                    <button title={r.active ? "Deactivate" : "Activate"} onClick={() => toggle(r)}
                      data-testid={`cm-toggle-${r.id}`} className="p-1 text-slate-500 hover:text-amber-600"><Power className="h-3.5 w-3.5" /></button>
                    <button title="Reassign products" onClick={() => setReassign({ src: r, target_id: "", then: "deactivate" })}
                      data-testid={`cm-reassign-${r.id}`} className="p-1 text-slate-500 hover:text-sky-600"><ArrowRightLeft className="h-3.5 w-3.5" /></button>
                    <button title="History" onClick={() => openHistory(r)}
                      data-testid={`cm-history-${r.id}`} className="p-1 text-slate-500 hover:text-slate-900"><History className="h-3.5 w-3.5" /></button>
                    <button title="Delete" onClick={() => del(r)}
                      data-testid={`cm-delete-${r.id}`} className="p-1 text-slate-500 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / edit */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid={`cm-dialog-${kind}`}>
          <DialogHeader><DialogTitle>{edit?.id ? `Edit ${singular}` : `New ${singular}`}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <label className="block space-y-1"><span className="text-[11px] text-slate-500">Name *</span>
                <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="cm-field-name" /></label>
              <label className="block space-y-1"><span className="text-[11px] text-slate-500">Display name</span>
                <Input value={edit.display_name} onChange={(e) => setEdit({ ...edit, display_name: e.target.value })} data-testid="cm-field-display" /></label>
              <label className="block space-y-1"><span className="text-[11px] text-slate-500">Description</span>
                <Input value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="cm-field-desc" /></label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !edit?.name?.trim()} data-testid="cm-save" className="bg-slate-900 hover:bg-slate-800">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign */}
      <Dialog open={!!reassign} onOpenChange={(o) => !o && setReassign(null)}>
        <DialogContent data-testid={`cm-reassign-dialog-${kind}`}>
          <DialogHeader><DialogTitle>Reassign products from “{reassign?.src?.name}”</DialogTitle></DialogHeader>
          {reassign && (
            <div className="space-y-3">
              <p className="text-[12px] text-slate-500">Move all products to another {singular.toLowerCase()}, then deactivate or delete the source. Historical records keep their new value.</p>
              <label className="block space-y-1"><span className="text-[11px] text-slate-500">Move to</span>
                <select value={reassign.target_id} onChange={(e) => setReassign({ ...reassign, target_id: e.target.value })}
                  data-testid="cm-reassign-target" className="w-full h-9 rounded-md border border-slate-200 px-2 text-[13px]">
                  <option value="">Select {singular.toLowerCase()}…</option>
                  {rows.filter((x) => x.id !== reassign.src.id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select></label>
              <label className="block space-y-1"><span className="text-[11px] text-slate-500">Then</span>
                <select value={reassign.then} onChange={(e) => setReassign({ ...reassign, then: e.target.value })}
                  data-testid="cm-reassign-then" className="w-full h-9 rounded-md border border-slate-200 px-2 text-[13px]">
                  <option value="deactivate">Deactivate source</option>
                  <option value="delete">Delete source</option>
                </select></label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassign(null)}>Cancel</Button>
            <Button onClick={doReassign} disabled={busy} data-testid="cm-reassign-confirm" className="bg-sky-600 hover:bg-sky-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reassign"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent data-testid={`cm-history-dialog-${kind}`}>
          <DialogHeader><DialogTitle>History — {history?.name}</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1.5">
            {(history?.items || []).length === 0 && <p className="text-[12px] text-slate-400">No history.</p>}
            {(history?.items || []).slice().reverse().map((h, i) => (
              <div key={i} className="text-[12px] flex items-center justify-between border-b border-slate-100 py-1">
                <span className="font-medium text-slate-700">{h.action}{h.changes ? ` (${h.changes.join(", ")})` : ""}</span>
                <span className="text-slate-400">{h.by} · {new Date(h.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CatalogueMasters() {
  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="catalogue-masters-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Category &amp; Brand Masters</h1>
        <p className="text-[13px] text-slate-500">One source of truth for the whole platform — manage without a developer.</p>
      </div>
      <Tabs defaultValue="categories">
        <TabsList data-testid="cm-tabs">
          <TabsTrigger value="categories" data-testid="cm-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="brands" data-testid="cm-tab-brands">Brands</TabsTrigger>
        </TabsList>
        <TabsContent value="categories" className="mt-4">
          <MasterTable kind="categories" icon={Layers} singular="Category" />
        </TabsContent>
        <TabsContent value="brands" className="mt-4">
          <MasterTable kind="brands" icon={Tag} singular="Brand" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
